import { useMemo } from 'react';

import { usePlansCatalog, ExplorePlanOffer } from 'app/hooks/useSubscriptionPlans/usePlansCatalog';
import { getNewTierPlans, NewTierPlan } from 'app/hooks/useSubscriptionPlans/getNewTierPlans';
import type { PricingCatalogErrorCode, PricingCatalogFeature } from 'app/services/pricingCatalog';
import { useIsJussubClient } from 'app/pages/PlanAndPayment/PlanUpgradeModal/hooks/useJussubPlanOffers';

import { getComparePlanPill } from './getComparePlanPill';
import { formatFeatureValue } from './formatFeatureValue';
import { COMPARE_PLANS_TABLE_ROWS } from './comparePlansTableData';
import type { ComparePlansColumn, ComparePlansTableRow } from './types';

// Derives the 6c table from each offer's `features` array (same list used,
// filtered, by the 6a summary). COMPARE_PLANS_TABLE_ROWS becomes the flag-OFF fallback: the
// hardcoded catalog has no per-feature availability data. Lower paid tiers still come in the
// offers payload (`can_subscribe: false`); `tierKeys` always carries all 3 columns so a tier
// missing from a partial catalog still falls back to '—' instead of an empty cell.
//
// Rows come from the UNION of every plan's feature codes, not just one reference plan's list.
// JusBill's hardcoded catalog always lists all codes per plan (unavailable ones included, just
// marked `availability: 'unavailable'`), so picking any single plan as the reference used to be
// equivalent to the union. JusSub breaks that assumption: each plan only lists the items it
// actually has registered, so a lower tier can have fewer codes than a higher one — picking it as
// the reference silently dropped every row the higher tiers have that it doesn't.
//
// JusSub não tem mock com todos os codes por plano, então um item ausente numa oferta existente
// não significa "indisponível": acima do One ele é contratável como add-on; no One, '—'.
const missingJussubFeatureAvailability = (
  key: NewTierPlan['key'],
): PricingCatalogFeature['availability'] => (key !== 'one' ? 'addon' : '--');

const buildRowsFromFeatures = (
  plans: ExplorePlanOffer[],
  tierKeys: NewTierPlan['key'][],
  isJussubClient: boolean,
): ComparePlansTableRow[] | null => {
  const featuresByPlanKey = new Map(
    plans.map((plan) => [plan.key, new Map(plan.features.map((feature) => [feature.code, feature]))]),
  );

  const referenceFeatureByCode = new Map<string, ExplorePlanOffer['features'][number]>();
  for (const plan of plans) {
    for (const feature of plan.features) {
      if (!referenceFeatureByCode.has(feature.code)) {
        referenceFeatureByCode.set(feature.code, feature);
      }
    }
  }
  if (referenceFeatureByCode.size === 0) return null;

  return [...referenceFeatureByCode.values()]
    .sort((a, b) => a.display_order - b.display_order)
    .map((referenceFeature) => ({
      attribute: referenceFeature.label,
      values: tierKeys.reduce((values, key) => {
        const planFeatures = featuresByPlanKey.get(key);
        const feature = planFeatures?.get(referenceFeature.code);
        if (feature) {
          values[key] = formatFeatureValue(feature);
        } else if (isJussubClient && planFeatures) {
          values[key] = formatFeatureValue({
            ...referenceFeature,
            availability: missingJussubFeatureAvailability(key),
          });
        } else {
          values[key] = '—';
        }
        return values;
      }, {} as Record<NewTierPlan['key'], string>),
    }));
};

export type ComparePlansTable = {
  isLoading: boolean;
  columns: ComparePlansColumn[];
  rows: ComparePlansTableRow[];
  error: PricingCatalogErrorCode | null;
  refetch: () => void;
  // Same offers ExplorePlansModal forwards to useHandleSelectNewTierPlan, so
  // checkout from the 6c table resolves gateway_id/coupon precedence instead of the hardcoded plan.
  offers: ExplorePlanOffer[];
};

// getNewTierPlans() is the always-3-tier skeleton for the 6c
// columns ("Comparar todos os planos" promises one column per tier, unlike the 6a grid which
// sizes itself to however many offers the backend returns). usePlansCatalog().plans overlays
// price/isCurrent/isUpgradeable per tier when the backend has an offer for it — a tier without
// one (customer already above it) still gets a column, just without an upgrade CTA.
export const useComparePlansTable = (): ComparePlansTable => {
  const { plans, isLoading, error, refetch } = usePlansCatalog();
  const isJussubClient = useIsJussubClient();

  return useMemo(() => {
    const catalogByKey = new Map(plans.map((plan) => [plan.key, plan]));
    const tierSkeleton = getNewTierPlans();

    const columns = tierSkeleton.map((fallbackPlan) => {
      const catalogPlan = catalogByKey.get(fallbackPlan.key);
      const isCurrent = Boolean(catalogPlan?.isCurrent);
      const isUpgradeable = Boolean(
        catalogPlan && !catalogPlan.isCurrent && !catalogPlan.isDowngrade,
      );

      return {
        key: fallbackPlan.key,
        title: catalogPlan?.title ?? fallbackPlan.title,
        price: catalogPlan?.price ?? fallbackPlan.price,
        pill: getComparePlanPill(fallbackPlan.key, isCurrent),
        isCurrent,
        isUpgradeable,
      };
    });

    const tierKeys = tierSkeleton.map((plan) => plan.key);
    const rows = buildRowsFromFeatures(plans, tierKeys, isJussubClient) ?? COMPARE_PLANS_TABLE_ROWS;

    return { isLoading, columns, rows, error, refetch, offers: plans };
  }, [plans, isLoading, error, refetch, isJussubClient]);
};
